import DOMPurify from "dompurify";

/**
 * Returns true if the string looks like a raw HTML email (template scaffolding present).
 * Distinguishes from simple user-formatted HTML snippets.
 */
export function isEmailHtml(content: string): boolean {
  return /<html[\s>]/i.test(content) || /mso-/i.test(content) || /preheader/i.test(content);
}

/**
 * Extracts readable preview content from a raw email HTML string.
 *
 * Strategy:
 * 1. Parse with DOMParser.
 * 2. Remove display:none elements (preheaders, spacers).
 * 3. Remove 1×1 tracking pixel <img> tags.
 * 4. Remove <style> and <script> blocks.
 * 5. If a .email-body-p cell exists, return its innerHTML (rich HTML preview).
 * 6. Fallback: return plain text (body.textContent) to avoid rendering unknown scaffolding.
 */
export function extractEmailPreview(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Remove hidden elements
  doc.querySelectorAll("*").forEach((el) => {
    const style = (el as HTMLElement).style;
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.maxHeight === "0px" ||
      style.maxHeight === "0"
    ) {
      el.remove();
    }
  });

  // Remove tracking pixels
  doc.querySelectorAll("img").forEach((img) => {
    const w = img.getAttribute("width");
    const h = img.getAttribute("height");
    if ((w === "1" && h === "1") || w === "0" || h === "0") img.remove();
  });

  // Remove style/script blocks
  doc.querySelectorAll("style, script").forEach((el) => el.remove());

  // Primary: extract known content cell
  const bodyCell = doc.querySelector(".email-body-p");
  if (bodyCell) {
    return bodyCell.innerHTML;
  }

  // Fallback: return inner HTML so whitespace and formatting tags are preserved
  return doc.body?.innerHTML?.trim() ?? "";
}

/**
 * Converts a plain-text string with \n newlines into an HTML string of <p> paragraphs.
 * Used when AI-generated draft bodies (stored as plain text) must be rendered via
 * dangerouslySetInnerHTML or loaded into TipTap, both of which require HTML input.
 *
 * Double newlines (\n\n) become paragraph breaks; single newlines become <br> within a paragraph.
 * Empty lines produce an empty <p></p> spacer to preserve visual separation.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return '';
  // Guard: already HTML — pass through untouched
  if (text.trimStart().startsWith('<')) return text;
  // Trim leading/trailing whitespace before splitting to avoid phantom empty paragraphs
  const paragraphs = text.trim().split(/\n{2,}/);
  return paragraphs
    .filter((para) => para.trim() !== '')
    .map((para) => {
      const inner = para
        .split('\n')
        .map((line) => line || '&#8203;') // zero-width space keeps empty lines renderable
        .join('<br>');
      return `<p>${inner}</p>`;
    })
    .join('');
}
